/**
 * API client — thin wrapper around fetch for our Hono API.
 * All requests go through /api/* which is proxied to Workers in dev.
 *
 * Mock mode: add ?mock=true to URL or set VITE_MOCK=true to use mock data.
 */

import {
  mockUser,
  getMockSummary,
  getMockDaily,
  getMockPosts,
  getMockHeatmap,
  mockBillingCheckout,
  mockBillingPortal,
} from "./mock-data";

/** Check if mock mode is active */
export const isMockMode =
  typeof window !== "undefined" &&
  (new URLSearchParams(window.location.search).get("mock") === "true" ||
    import.meta.env.VITE_MOCK === "true");

const API_BASE = "/api";

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    ...options,
  });

  const data = await res.json();

  if (!res.ok) {
    throw new ApiError(
      data?.error?.message || "Request failed",
      res.status,
      data?.error?.code || "UNKNOWN"
    );
  }

  return data.data as T;
}

// ── Auth ──
export interface UserData {
  id: string;
  username: string;
  name: string | null;
  profilePictureUrl: string | null;
  subscriptionStatus: string;
  timezone: string;
  createdAt: number;
  accounts: AccountData[];
}

export interface AccountData {
  id: string;
  threadsUserId: string;
  username: string;
  name: string | null;
  profilePictureUrl: string | null;
  isPrimary: boolean;
  lastSyncedAt: number | null;
  syncEnabled: boolean;
}

export const authApi = {
  me: (): Promise<UserData> =>
    isMockMode ? delay(mockUser) : request<UserData>("/auth/me"),
  logout: (): Promise<{ success: boolean }> =>
    isMockMode ? delay({ success: true }) : request("/auth/logout", { method: "POST" }),
  token: (): Promise<{ token: string }> =>
    isMockMode
      ? delay({ token: "mock-token" })
      : request("/auth/token", { method: "POST" }),
};

// ── Insights ──
export interface InsightsSummary {
  followersCount: number;
  newFollowers: number;
  views: number;
  likes: number;
  replies: number;
  reposts: number;
  quotes: number;
  period: string;
  startDate: string;
  endDate: string;
}

export interface DailyData {
  metrics: Array<{ date: string; value: number }>;
  metric: string;
  period: string;
  startDate: string;
  endDate: string;
}

export const insightsApi = {
  summary: (accountId: string, period: string): Promise<InsightsSummary> =>
    isMockMode
      ? delay(getMockSummary(period))
      : request(`/insights/${accountId}/summary?period=${period}`),
  daily: (accountId: string, period: string, metric: string): Promise<DailyData> =>
    isMockMode
      ? delay(getMockDaily(period, metric))
      : request(`/insights/${accountId}/daily?period=${period}&metric=${metric}`),
};

// ── Posts ──
export interface PostData {
  id: string;
  text: string | null;
  mediaType: string;
  permalink: string | null;
  publishedAt: number;
  views: number;
  likes: number;
  replies: number;
  reposts: number;
  quotes: number;
  shares: number;
}

export interface PostsData {
  posts: PostData[];
  total: number;
  page: number;
  limit: number;
  period: string;
}

export const postsApi = {
  list: (accountId: string, period: string, sort: string, page: number): Promise<PostsData> =>
    isMockMode
      ? delay(getMockPosts(period, sort, page))
      : request(`/posts/${accountId}?period=${period}&sort=${sort}&page=${page}&limit=20`),
};

// ── Heatmap ──
export interface HeatmapCell {
  day: number;
  hour: number;
  likes: number;
  replies: number;
  total: number;
}

export interface HeatmapData {
  cells: HeatmapCell[];
  period: string;
  startDate: string;
  endDate: string;
}

export const heatmapApi = {
  data: (accountId: string, period: string): Promise<HeatmapData> =>
    isMockMode
      ? delay(getMockHeatmap(period))
      : request(`/heatmap/${accountId}?period=${period}`),
};

// ── Accounts ──
export const accountsApi = {
  list: (): Promise<AccountData[]> =>
    isMockMode ? delay(mockUser.accounts) : request("/accounts"),
  connect: (): Promise<{ authUrl: string }> =>
    isMockMode
      ? delay({ authUrl: "#mock-oauth" })
      : request("/accounts/connect", { method: "POST" }),
  disconnect: (id: string): Promise<{ success: boolean }> =>
    isMockMode
      ? delay({ success: true })
      : request(`/accounts/${id}`, { method: "DELETE" }),
  sync: (accountId: string): Promise<{ message: string; accountId: string }> =>
    isMockMode
      ? delay({ message: "Sync queued", accountId })
      : request(`/accounts/${accountId}/sync`, { method: "POST" }),
};

// ── Billing ──
export interface CheckoutData {
  paddlePriceId: string;
  paddleClientToken: string;
  paddleCustomerId: string | null;
  userId: string;
  paddleEnvironment: string;
}

export const billingApi = {
  checkout: (): Promise<CheckoutData> =>
    isMockMode
      ? delay(mockBillingCheckout)
      : request("/billing/checkout", { method: "POST" }),
  portal: (): Promise<{ url: string }> =>
    isMockMode
      ? delay(mockBillingPortal)
      : request("/billing/portal", { method: "POST" }),
};

// ── Mock helper ──

/** Simulate network delay for mock responses */
function delay<T>(data: T, ms = 300): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(data), ms));
}
